/**
 * @module medication-catalog
 * @description Servicio para gestionar el vademécum de medicamentos vía API REST.
 */
import api from "@/services/api";

const endpoint = "/medications";

/**
 * @description Consulta medicamentos con filtros y paginación
 * @param {Object} params - Parámetros de búsqueda
 * @returns {Promise<{docs: Array, total: number}>} Resultado paginado
 */
function query(params) {
  return api.get(endpoint, { params }).then((res) => res.data);
}

/**
 * @description Obtiene un medicamento por ID
 * @param {string} id - ID del medicamento
 * @returns {Promise<Object>} Medicamento
 */
function getById(id) {
  return api.get(`${endpoint}/${id}`).then((res) => res.data);
}

/**
 * @description Crea un nuevo medicamento
 * @param {Object} data - Datos del medicamento
 * @param {string} data.commercialName - Nombre comercial
 * @param {string} data.activeIngredient - Droga/principio activo
 * @param {string} data.presentation - Presentación
 * @param {string} data.laboratory - Laboratorio
 * @param {string} data.category - Categoría
 * @param {boolean} data.requiresPrescription - Requiere receta
 * @param {boolean} data.isControlled - Es controlado
 * @returns {Promise<Object>} Medicamento creado
 */
function create(data) {
  return api.post(endpoint, data).then((res) => res.data);
}

/**
 * @description Actualiza un medicamento existente
 * @param {string} id - ID del medicamento
 * @param {Object} data - Campos a actualizar
 * @returns {Promise<Object>} Medicamento actualizado
 */
function update(id, data) {
  return api.put(`${endpoint}/${id}`, data).then((res) => res.data);
}

/**
 * @description Elimina un medicamento
 * @param {string} id - ID del medicamento
 * @returns {Promise<void>}
 */
function remove(id) {
  return api.delete(`${endpoint}/${id}`);
}

/**
 * @description Devuelve alternativas de medicamento (MOCK).
 *              Será reemplazado por servicio externo.
 * @param {string} drugName - Nombre del fármaco recetado
 * @returns {Promise<Array>} Lista de alternativas con precio, cobertura, stock
 */
function getAlternatives(drugName) {
  const mockData = [
    {
      id: "med-original",
      name: drugName || "Medicamento recetado",
      presentation: "Comprimidos x 30",
      lab: "Laboratorio Original",
      type: "original",
      price: 18500,
      coveragePercent: 40,
      copay: 11100,
      inStock: true,
    },
    {
      id: "med-generic-1",
      name: (drugName || "Medicamento") + " MK",
      presentation: "Comprimidos x 30",
      lab: "Laboratorio Genérico",
      type: "generic",
      price: 9200,
      coveragePercent: 60,
      copay: 3680,
      inStock: true,
    },
    {
      id: "med-similar-1",
      name: (drugName || "Medicamento") + " Plus",
      presentation: "Comprimidos x 30",
      lab: "Laboratorio Similar",
      type: "similar",
      price: 14800,
      coveragePercent: 40,
      copay: 8880,
      inStock: true,
    },
    {
      id: "med-generic-2",
      name: (drugName || "Medicamento") + " Bago",
      presentation: "Comprimidos x 30",
      lab: "Laboratorios Bagó",
      type: "generic",
      price: 8700,
      coveragePercent: 60,
      copay: 3480,
      inStock: false,
    },
  ];
  return Promise.resolve(mockData);
}

export default {
  query,
  getById,
  create,
  update,
  remove,
  getAlternatives,
};
